import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Box } from '@mui/material';
import { isBefore, isValid, startOfDay } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';

import { CustomDateRangePickerProps } from './types';
import {
  DatePickersBox,
  DatePickerWrapper,
  ErrorText,
  PickersContainer,
  StyledClearButton,
  StyledDatePicker,
  TranslationModeSpan,
} from './styles';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { createDateValidators } from './helpers';
import { CustomTooltip } from '../../Tooltip/Tooltip';
import { customPalette } from '../../../../styles/theme';

const CustomDateRangePicker = forwardRef<
  { isTimeValid: () => boolean },
  CustomDateRangePickerProps
>(
  (
    {
      onChange,
      value,
      disabled,
      allowPastDates = false,
      defaultToToday = true,
      requireTime = false,
    },
    ref
  ) => {
    const { isTranslationMode, handleKeyClick } = useI18n();
    const currentDate = startOfDay(new Date());

    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const [startTimePicked, setStartTimePicked] = useState(false);
    const [endTimePicked, setEndTimePicked] = useState(false);
    const { t } = useI18n();

    const isTimeValid = () => {
      if (!requireTime) return true;
      return startTimePicked && endTimePicked;
    };
    useImperativeHandle(ref, () => ({
      isTimeValid,
    }));

    useEffect(() => {
      if (defaultToToday && !value) {
        onChange?.([currentDate, null]);
      }
    }, [defaultToToday, value, onChange, currentDate]);

    const handleStartDateChange = (newDate: Date | null) => {
      const error = !newDate || !isValid(newDate);
      setStartDateError(error);

      if (newDate && value[1] && isBefore(value[1], newDate)) {
        onChange?.([newDate, null]);
      } else {
        onChange?.([newDate, value[1]]);
      }
    };

    const handleEndDateChange = (newDate: Date | null) => {
      const error = !newDate || !isValid(newDate);
      setEndDateError(error);
      onChange?.([value[0], newDate]);
    };

    const handleTimeChange = (type: 'start' | 'end', newTime: Date | null) => {
      if (type === 'start') {
        setStartTimePicked(true);
      } else {
        setEndTimePicked(true);
      }
      if (onChange) {
        switch (type) {
          case 'start':
            onChange([newTime, value[1]]);
            break;
          case 'end':
            onChange([value[0], newTime]);
        }
      }
    };

    const handleClearDates = () => {
      onChange?.([null, null]);
      setStartDateError(false);
      setEndDateError(false);

      if (requireTime) {
        setStartTimePicked(false);
        setEndTimePicked(false);
      }
    };

    const { shouldDisableStartDate, shouldDisableEndDate } =
      createDateValidators(
        currentDate,
        value[0],
        value[1],
        allowPastDates,
        disabled || false
      );

    const showClearButton =
      !disabled &&
      (value[0] ||
        value[1] ||
        (requireTime && (startTimePicked || endTimePicked)));

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <PickersContainer>
          <DatePickerWrapper>
            <DatePickersBox>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <StyledDatePicker
                    label={t(TranslationKeys.dateRangePicker_start_date)}
                    value={value[0]}
                    onChange={handleStartDateChange}
                    shouldDisableDate={shouldDisableStartDate}
                    disabled={disabled}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: startDateError,
                      },
                    }}
                    slots={{
                      openPickerIcon: () => (
                        <CalendarTodayIcon
                          style={{
                            color: disabled
                              ? customPalette.grey[300]
                              : customPalette.blue.main,
                          }}
                        />
                      ),
                    }}
                  />
                  {startDateError && (
                    <ErrorText>
                      {t(TranslationKeys.dateRangePicker_enter_correct_date)}
                    </ErrorText>
                  )}

                  {requireTime && (
                    <TimePicker
                      label={t(TranslationKeys.daterangepicker_start_time)}
                      ampm={false}
                      value={value[0]}
                      onChange={(newTime) => handleTimeChange('start', newTime)}
                      disabled={disabled}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          size: 'small',
                        },
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <StyledDatePicker
                    label={t(TranslationKeys.dateRangePicker_end_date)}
                    value={value[1]}
                    onChange={handleEndDateChange}
                    shouldDisableDate={shouldDisableEndDate}
                    disabled={disabled || !value[0]}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: endDateError,
                      },
                    }}
                    slots={{
                      openPickerIcon: () => (
                        <CalendarTodayIcon
                          style={{
                            color: disabled
                              ? customPalette.grey[300]
                              : customPalette.blue.main,
                          }}
                        />
                      ),
                    }}
                  />
                  {endDateError && (
                    <ErrorText>
                      {t(TranslationKeys.dateRangePicker_enter_correct_date)}
                    </ErrorText>
                  )}

                  {requireTime && (
                    <TimePicker
                      label={t(TranslationKeys.daterangepicker_end_time)}
                      ampm={false}
                      value={value[1]}
                      onChange={(newTime) => handleTimeChange('end', newTime)}
                      disabled={disabled || !value[1]}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          size: 'small',
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </DatePickersBox>

            {showClearButton &&
              (isTranslationMode ? (
                <TranslationModeSpan
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleKeyClick(TranslationKeys.dateRangePicker_clear_dates);
                  }}
                >
                  {TranslationKeys.dateRangePicker_clear_dates}
                </TranslationModeSpan>
              ) : (
                <CustomTooltip
                  text={
                    t(TranslationKeys.dateRangePicker_clear_dates) as string
                  }
                  placement="left"
                  showIcon={false}
                >
                  <StyledClearButton
                    onClick={handleClearDates}
                    size="small"
                    disabled={disabled}
                  >
                    <ClearIcon />
                  </StyledClearButton>
                </CustomTooltip>
              ))}
          </DatePickerWrapper>
        </PickersContainer>
      </LocalizationProvider>
    );
  }
);

export default CustomDateRangePicker;
